import { useCallback, useEffect, useState } from 'react';

import { ApiError } from '@taxi/api-client';

import { useDispatcherHub } from '../realtime/useDispatcherHub';
import { messagesApi, type Thread } from './api';

/**
 * Every driver's thread, kept current over the hub.
 *
 * Refetched on every change rather than patched from the event: the list is a handful of
 * rows, the server owns its order and its unread counts, and a client that maintained its
 * own copy would eventually disagree with the one a colleague's desk sees.
 *
 * `enabled` is false once the server has said the firm does not message its drivers, which
 * is how the card and the pill disappear without the console deciding anything itself.
 */
export function useThreads() {
  const [threads, setThreads] = useState<Thread[]>([]);
  const [enabled, setEnabled] = useState(true);

  const refresh = useCallback(async () => {
    try {
      setThreads(await messagesApi.threads());
      setEnabled(true);
    } catch (failure) {
      if (failure instanceof ApiError && failure.problem.code === 'message.disabled') {
        setEnabled(false);
        setThreads([]);
      }

      // Anything else is a moment without a connection; the list stays as it was.
    }
  }, []);

  // An async fetch rather than a call to refresh(), so nothing sets state on the render
  // that scheduled this — the shape every card here uses.
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const current = await messagesApi.threads();

        if (!cancelled) setThreads(current);
      } catch (failure) {
        if (!cancelled && failure instanceof ApiError && failure.problem.code === 'message.disabled') {
          setEnabled(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useDispatcherHub({
    onDriverMessageChanged: () => void refresh(),
    onReconnected: () => void refresh(),
  });

  const unread = threads.reduce((sum, thread) => sum + thread.unreadFromDriver, 0);

  return { threads, unread, enabled, refresh };
}
